import { AnimatePresence } from 'framer-motion';
import { FC, memo, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import useStorageToggle from '@/hooks/useStorageToggle';
import { OptimisationTestUnitResource } from '@/types/resources';
import {
  OptimisationProjectNetworkEnum,
  OptimisationTestTypeEnum,
} from '@/utils/enums';

import DeleteAllUnitsButton from './DeleteAllUnitsButton';
import FinalisationFormModal from './FinalisationFormModal';
import GoogleAdsExportButton from './GoogleAdsExportButton';
import TestHeading from './TestHeading';
import TestTable from './TestTable';
import TestsStatusHolder from './TestsStatusHolder';

const ButtonWrapper = styled.div`
  justify-content: flex-end;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  > button {
    padding-left: 16px;
    padding-right: 16px;
    font-weight: 600;
  }
`;

interface Props {
  tests?: OptimisationTestUnitResource[];
  network: OptimisationProjectNetworkEnum;
  externalProjectId: string;
  projectTitle: string;
  isForTitles?: boolean;
}

const Finalisation: FC<Props> = memo(
  ({
    tests,
    network,
    externalProjectId,
    projectTitle,
    isForTitles = false,
  }) => {
    const [showTestModal, setShowTestModal] = useState(false);
    const [isOpen, toggleIsOpen] = useStorageToggle(
      `optimisation_test_${externalProjectId}_${isForTitles ? 'titles' : 'finalisation'}`,
    );

    const type = isForTitles
      ? OptimisationTestTypeEnum.TITLE
      : OptimisationTestTypeEnum.FINALISATION;

    const batches = useMemo(
      () =>
        tests!.reduce<number[]>((acc, curr) => {
          if (acc.includes(curr.batch)) {
            return acc;
          }

          return [...acc, curr.batch];
        }, []),
      [tests],
    );

    return (
      <>
        <TestHeading
          heading={`Finalisation`}
          subheading={isForTitles ? ' · Title' : ''}
          description={
            isForTitles
              ? 'Tests which video titles, with the finalized video and thumbnail, attract the most viewer engagement'
              : 'Combines the best intro/sequence with top thumbnails to see which mix maximizes viewer engagement'
          }
          onToggle={toggleIsOpen}
          isOpen={isOpen}
        />
        {isOpen && (
          <>
            <TestTable
              type={type}
              network={network}
              tests={tests}
              testParameter="estimatedFinalValue"
            />
            <ButtonWrapper>
              <TestsStatusHolder tests={tests!} />
              <GoogleAdsExportButton
                type={type}
                externalProjectId={externalProjectId}
              />
              <DeleteAllUnitsButton type={type} batches={batches} />
              <Button onClick={() => setShowTestModal(true)} $type="colorized">
                Upload tests
              </Button>
            </ButtonWrapper>
            <AnimatePresence>
              {showTestModal && (
                <FinalisationFormModal
                  isForTitles={isForTitles}
                  projectTitle={projectTitle}
                  onClose={() => setShowTestModal(false)}
                  network={network}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </>
    );
  },
);

export default Finalisation;
